import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Crown, Linkedin } from "lucide-react";
import { api } from "@/api";
import Reveal from "@/components/Reveal";
import Portrait from "@/components/Portrait";
import NotFound from "@/pages/NotFound";

export default function TeamProfile() {
  const { slug } = useParams();
  const [member, setMember] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMember(null);
    setMissing(false);
    api.get(`/team/by-slug/${slug}`)
      .then((r) => setMember(r.data))
      .catch(() => setMissing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (missing) return <NotFound />;
  if (!member) return <div className="min-h-screen bg-navy-950" data-testid="team-profile-loading" />;

  const paragraphs = (member.bio || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const [firstPara, ...restParas] = paragraphs;

  return (
    <div data-testid={`team-profile-${slug}`}>
      <section className="bg-navy-950 pb-24 pt-40 lg:pt-48" data-testid="team-profile-hero">
        <div className="container-rcs">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-10 flex items-center gap-2 text-xs text-slate-400" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-gold">Home</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <Link to="/team" className="transition-colors hover:text-gold">Meet The Team</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <span className="text-gold">{member.fullName}</span>
            </nav>
            <div className="grid items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <div className="relative inline-block">
                  <Portrait name={member.fullName} src={member.photograph} size="xl" testid="team-profile-photo" />
                  {member.isLead && (
                    <span className="absolute -bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-gold px-4 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-navy-950">
                      <Crown className="h-3 w-3" aria-hidden /> Team Lead
                    </span>
                  )}
                </div>
              </div>
              <div className="lg:col-span-8">
                <span className="eyebrow-light">{member.department}</span>
                <h1 className="mt-3 font-serif text-4xl text-ivory sm:text-5xl">{member.fullName}</h1>
                <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-gold">{member.designation}</p>
                {firstPara && (
                  <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300" data-testid="team-profile-intro">
                    {firstPara}
                  </p>
                )}
                {member.linkedinURL && (
                  <a
                    href={member.linkedinURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="team-profile-linkedin"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-xs font-semibold text-slate-200 transition-colors hover:border-gold hover:text-gold"
                  >
                    <Linkedin className="h-4 w-4" aria-hidden /> LinkedIn Profile
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="team-profile-body">
        <div className="container-rcs grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="eyebrow">Full Profile</h2>
            {restParas.length > 0 ? (
              <div className="mt-6 space-y-6" data-testid="team-profile-bio">
                {restParas.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-slate-600">{p}</p>
                ))}
              </div>
            ) : !firstPara ? (
              <p className="mt-6 max-w-xl rounded-2xl border border-dashed border-navy-900/20 bg-white px-8 py-10 text-sm italic leading-relaxed text-slate-500" data-testid="team-profile-placeholder">
                {member.fullName}'s detailed profile will be published soon.
              </p>
            ) : null}
          </div>
          <Reveal delay={0.15} className="lg:col-span-4">
            <div className="sticky top-28 rounded-2xl bg-navy-950 p-8" data-testid="team-profile-sidebar">
              <p className="eyebrow-light">At a Glance</p>
              <dl className="mt-6 space-y-5 text-sm">
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Name</dt>
                  <dd className="mt-1 font-semibold text-ivory">{member.fullName}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Designation</dt>
                  <dd className="mt-1 font-semibold text-ivory">{member.designation}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Department</dt>
                  <dd className="mt-1 font-semibold text-ivory">{member.department}</dd>
                </div>
                {member.isLead && (
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Role</dt>
                    <dd className="mt-1 font-semibold text-gold">Team Lead</dd>
                  </div>
                )}
              </dl>
              <Link to="/team" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold transition-colors hover:text-gold-light" data-testid="team-profile-back">
                <ArrowLeft className="h-4 w-4" aria-hidden /> Meet The Team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
